import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { creativeAiApi } from '../api/CreativeAiApi'
import type { CreativeAiSectionQuery } from '../api/CreativeAiApi'
import type {
  WhatsWorkingMetric,
  WhatsWorkingQuery,
  WhatsWorkingResponse,
  WhatsWorkingScope,
} from '../models/whatsWorking.types'
import { decodeWhatsWorkingResponse } from '../service/whatsWorking'

import { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

export interface WhatsWorkingLoadInput {
  scope: WhatsWorkingScope
  query: WhatsWorkingQuery
}

function requestKey({ scope, query }: WhatsWorkingLoadInput): string {
  return [
    scope.accountId,
    scope.workspaceId,
    query.kind,
    query.platform ?? '',
    query.objective ?? '',
    query.campaignType ?? '',
    query.analysisId,
    query.dateFrom,
    query.dateTo,
  ]
    .map(String)
    .join('|')
}

function apiQuery(query: WhatsWorkingQuery): CreativeAiSectionQuery {
  return {
    type: query.kind,
    analysis_id: query.analysisId,
    date_start: query.dateFrom,
    date_end: query.dateTo,
    ...(query.platform ? { platform: query.platform } : {}),
    ...(query.objective ? { objective: query.objective } : {}),
    ...(query.campaignType ? { campaign_type: query.campaignType } : {}),
  }
}

function errorFrom(cause: unknown): ApiError {
  return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
}

function canceled(cause: unknown): boolean {
  return ApiError.isApiError(cause) && cause.kind === 'canceled'
}

/**
 * Legacy parity (`CreativePerformingAnalysis.vue`'s `getData`): a metric the backend returns with
 * no `working`/`not_working` rows for this account is excluded from the default pick entirely —
 * otherwise the summary cards and outliers table blank out even though other metrics have data.
 */
function defaultMetricField(metrics: readonly WhatsWorkingMetric[]): string | null {
  return (
    metrics.find((metric) => metric.working.length > 0 || metric.not_working.length > 0)?.metric ??
    null
  )
}

/**
 * The sole owner of the `whats_working` request. Its key includes the visible scope so
 * sibling overview panels cannot duplicate the same backend call or apply stale results.
 */
export const useWhatsWorkingStore = defineStore('creativeAiWhatsWorking', () => {
  const response = shallowRef<WhatsWorkingResponse>({ metrics: [], dropdownables: {} })
  const state = ref<UiAsyncState>('empty')
  const error = shallowRef<ApiError | null>(null)
  const selectedMetricField = ref<string | null>(null)
  let activeKey: string | null = null
  let generation = 0
  let controller: AbortController | null = null
  let pending: Promise<void> | null = null

  const metricFields = computed(() => response.value.metrics.map((metric) => metric.metric))
  const selectedMetric = computed(
    () =>
      response.value.metrics.find((metric) => metric.metric === selectedMetricField.value) ?? null,
  )
  const working = computed(() => selectedMetric.value?.working ?? [])
  const notWorking = computed(() => selectedMetric.value?.not_working ?? [])
  const simulatableMetrics = computed(() =>
    response.value.metrics
      .filter((metric) =>
        metric.not_working.some((row) => row.what_if !== null && row.what_if.reason === null),
      )
      .map((metric) => metric.metric),
  )

  function byMetric(field: string | null) {
    return response.value.metrics.find((metric) => metric.metric === field) ?? null
  }

  function selectMetric(field: string | null): void {
    selectedMetricField.value = field && metricFields.value.includes(field) ? field : null
  }

  async function load(input: WhatsWorkingLoadInput): Promise<void> {
    const nextKey = requestKey(input)
    if (activeKey === nextKey && pending) return pending
    if (activeKey === nextKey && (state.value === 'ready' || state.value === 'empty')) return

    controller?.abort()
    const requestGeneration = ++generation
    const nextController = new AbortController()
    controller = nextController
    activeKey = nextKey
    state.value = 'loading'
    error.value = null
    const request = (async () => {
      try {
        const raw = await creativeAiApi.getWhatsWorking(
          input.scope,
          apiQuery(input.query),
          nextController.signal,
        )
        if (requestGeneration !== generation || nextController.signal.aborted) return
        response.value = decodeWhatsWorkingResponse(raw)
        if (!metricFields.value.includes(selectedMetricField.value ?? '')) {
          selectedMetricField.value = defaultMetricField(response.value.metrics)
        }
        state.value = response.value.metrics.length ? 'ready' : 'empty'
      } catch (cause) {
        if (requestGeneration !== generation || nextController.signal.aborted || canceled(cause))
          return
        error.value = errorFrom(cause)
        state.value = 'error'
      } finally {
        if (requestGeneration === generation) pending = null
      }
    })()
    pending = request
    return request
  }

  function cancel(): void {
    controller?.abort()
    controller = null
    pending = null
  }

  function resetState(): void {
    cancel()
    generation += 1
    activeKey = null
    response.value = { metrics: [], dropdownables: {} }
    state.value = 'empty'
    error.value = null
    selectedMetricField.value = null
  }

  return {
    response,
    state,
    error,
    selectedMetricField,
    metricFields,
    selectedMetric,
    working,
    notWorking,
    simulatableMetrics,
    byMetric,
    selectMetric,
    load,
    cancel,
    resetState,
  }
})
